import type { IssueDetail, TodoIssueCard as IssueCard } from '@cortex/domain-ac-mens-hub-api';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Textarea } from '@cortex/web-design-system/components/ui/Textarea';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useIssueDetail, useIssueMgmtWrite } from '../../hooks/useIssueMgmt.js';
import { useIssueCases } from '../../hooks/useIssueTodo.js';
import { kianText, replyDraft } from '../../lib/issue-mgmt-copy.js';
import { formatShortDate } from '../../lib/issue-mgmt-format.js';
import { cardQuestion } from '../../lib/issue-mgmt-next-action.js';
import { DecideDialog, type DecisionChoice } from './DecideDialog.js';
import { DoneDialog } from './DoneDialog.js';
import { LinkCaseDialog } from './LinkCaseDialog.js';
import {
  Actions,
  CardSection,
  CaseCheckSection,
  JudgeButtons,
  MissingSection,
  RecentVoices,
  StartNote,
  RedecideBanner,
  VerifySection,
  VoiceLine,
  WhatSection,
} from './TodoIssueBodies.js';
import type { CardOutcome } from './TodoTriageCard.js';
import { useIssueToast } from './useIssueToast.js';

/**
 * 開いている小窓。
 * @graph-connects none
 */
type OpenDialog =
  | { kind: 'decide'; decision: DecisionChoice }
  | { kind: 'link' }
  | { kind: 'done' }
  | null;

/**
 * やることの判定・確認のカード（課題1件の次にやること）。中身は課題の詳細（と案件欄）を読んで出す。
 *
 * 判定（判定する・判定し直す・必須項目）は対応する・保留・対応しないの小窓（再浮上と判定の変更なら「このままでよい」も）、
 * 確認は種類ごとに大ボタン1つ（案件を紐づける＋起案用にコピー／Backlogで確かめた／直った・まだ直っていない／
 * 返信済みにする＋コピー／対応した＋まだ対応中・判定を見直す）。押すと保存して次のカードへ進み、トーストから元に戻せる。
 * 週次の日は、判定のカードを開いたことを記録する（所要時間の記録。ボタンは置かない）。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 判定者と担当が、課題1件の次にやること（判定・必須項目・起案・案件の確認・直ったか・結果を返す・着手したか）をカード1枚で片づける
 * @graph-connects useIssueDetail [calls] 課題の詳細
 * @graph-connects useIssueCases [calls] 案件欄
 * @graph-connects useIssueMgmtWrite [calls] acknowledge・checkCase・confirmFixed・markNotFixed・markReplied・markInProgress・recordReviewOpen
 * @graph-connects DecideDialog [calls] 判定の小窓
 * @graph-connects LinkCaseDialog [calls] 案件を紐づける小窓
 * @graph-connects DoneDialog [calls] 対応したの小窓
 * @graph-connects JudgeButtons [calls] 判定のボタン
 */
export function TodoIssueCard({
  card,
  done,
  outcome,
  now,
}: {
  card: IssueCard;
  done: boolean;
  outcome: CardOutcome;
  now: Date;
}) {
  const { t } = useTranslation();
  const toast = useIssueToast();
  const next = card.next;
  const detail = useIssueDetail(card.issueId);
  const cases = useIssueCases(
    card.issueId,
    next.kind === 'check_case' || next.kind === 'verify_fix',
  );
  const recordOpen = useIssueMgmtWrite('recordReviewOpen');
  const acknowledge = useIssueMgmtWrite('acknowledge');
  const checkCase = useIssueMgmtWrite('checkCase');
  const confirmFixed = useIssueMgmtWrite('confirmFixed');
  const markNotFixed = useIssueMgmtWrite('markNotFixed');
  const markReplied = useIssueMgmtWrite('markReplied');
  const markInProgress = useIssueMgmtWrite('markInProgress');
  const [dialog, setDialog] = useState<OpenDialog>(null);
  const [draft, setDraft] = useState<string | null>(null);

  // 週次の日の所要時間のため、判定のカードを開いたことを1回だけ記録する（失敗しても画面は止めない）
  const recorded = useRef(false);
  const { mutate: recordOpenMutate } = recordOpen;
  useEffect(() => {
    if (recorded.current || done || card.type !== 'judge') return;
    recorded.current = true;
    recordOpenMutate({ issueId: card.issueId });
  }, [card.issueId, card.type, done, recordOpenMutate]);

  if (detail.isError) {
    return (
      <p className="px-5 py-6 text-sm text-[var(--color-error)]">
        {t('issueMgmt.common.loadError')}
      </p>
    );
  }
  if (detail.data === undefined) {
    return (
      <p className="px-5 py-6 text-sm text-[var(--color-text-muted)]">
        {t('issueMgmt.common.loading')}
      </p>
    );
  }
  const issue: IssueDetail = detail.data;
  const busy =
    acknowledge.isPending ||
    checkCase.isPending ||
    confirmFixed.isPending ||
    markNotFixed.isPending ||
    markReplied.isPending ||
    markInProgress.isPending;

  /** その場で記録する操作の共通の結び（済みにしてトースト）。 */
  const finish = (
    label: string,
    message: string,
    operationId: string | null,
    detailText?: string,
  ) => {
    outcome.onDone(label, operationId);
    toast.notifySaved(message, {
      operationId,
      ...(detailText === undefined ? {} : { detail: detailText }),
    });
  };

  const copy = (text: string, message: string, detailText?: string) => {
    void navigator.clipboard.writeText(text).then(() => {
      toast.notifySaved(message, detailText === undefined ? {} : { detail: detailText });
    }, toast.notifyError);
  };

  const replyTargets = issue.voices.filter(
    (voice) => voice.postedAt !== null && voice.repliedAt === null,
  );

  const judgeButtons = (keep: boolean) => (
    <JudgeButtons
      onDecide={(decision) => {
        setDialog({ kind: 'decide', decision });
      }}
      keep={
        keep
          ? {
              loading: acknowledge.isPending,
              onKeep: () => {
                acknowledge.mutate(
                  {
                    issueId: issue.id,
                    base: { decidedAt: issue.decidedAt, reviewedAt: issue.reviewedAt },
                  },
                  {
                    onSuccess: (result) => {
                      finish(
                        t('issueMgmt.card.kept'),
                        t('issueMgmt.card.keptSaved', { title: issue.title }),
                        result.operationId,
                      );
                    },
                    onError: toast.notifyError,
                  },
                );
              },
            }
          : null
      }
    />
  );

  const body = (() => {
    switch (next.kind) {
      case 'decide':
        return {
          content: (
            <>
              <WhatSection issue={issue} />
              <RecentVoices issue={issue} now={now} />
            </>
          ),
          actions: judgeButtons(false),
        };
      case 'redecide':
        return {
          content: (
            <>
              <RedecideBanner issue={issue} next={next} now={now} />
              <WhatSection issue={issue} />
              <RecentVoices issue={issue} now={now} />
            </>
          ),
          actions: judgeButtons(true),
        };
      case 'fill':
        return {
          content: (
            <>
              <MissingSection missing={next.missing} />
              <WhatSection issue={issue} />
            </>
          ),
          actions: (
            <Button
              size="lg"
              fullWidth
              onClick={() => {
                setDialog({ kind: 'decide', decision: 'accepted' });
              }}
            >
              {t('issueMgmt.card.fillAction')}
            </Button>
          ),
        };
      case 'link_case':
        return {
          content: (
            <>
              <WhatSection issue={issue} />
              <p className="px-5 pb-3 text-xs text-[var(--color-text-muted)]">
                {next.unresolvedAt === null
                  ? t('issueMgmt.card.linkNote', {
                      name: issue.assignee?.displayName ?? t('issueMgmt.common.dash'),
                    })
                  : t('issueMgmt.card.linkNoteUnresolved', {
                      date: formatShortDate(next.unresolvedAt, now),
                    })}
              </p>
            </>
          ),
          actions: (
            <>
              <Button
                size="lg"
                fullWidth
                onClick={() => {
                  setDialog({ kind: 'link' });
                }}
              >
                {t('issueMgmt.next.buttonLinkCase')}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  copy(kianText(issue, window.location.origin, t), t('issueMgmt.card.kianCopied'));
                }}
              >
                {t('issueMgmt.card.kianCopy')}
              </Button>
            </>
          ),
        };
      case 'check_case':
        return {
          content: (
            <CaseCheckSection
              next={next}
              caseRow={cases.data?.cases.find((c) => c.caseKey === next.caseKey)}
              now={now}
            />
          ),
          actions: (
            <Button
              size="lg"
              fullWidth
              loading={checkCase.isPending}
              disabled={busy}
              onClick={() => {
                checkCase.mutate(
                  { issueId: issue.id, caseKey: next.caseKey },
                  {
                    onSuccess: (result) => {
                      finish(
                        t('issueMgmt.next.buttonChecked'),
                        t('issueMgmt.card.checkedSaved', { title: issue.title }),
                        result.operationId,
                        result.recorded ? undefined : t('issueMgmt.card.checkedRetry'),
                      );
                    },
                    onError: toast.notifyError,
                  },
                );
              }}
            >
              {t('issueMgmt.next.buttonChecked')}
            </Button>
          ),
        };
      case 'verify_fix':
        return {
          content: <VerifySection issue={issue} cases={cases.data?.cases ?? []} now={now} />,
          actions: (
            <div className="flex flex-wrap gap-2">
              <Button
                size="lg"
                loading={confirmFixed.isPending}
                disabled={busy}
                onClick={() => {
                  confirmFixed.mutate(
                    { issueId: issue.id },
                    {
                      onSuccess: (result) => {
                        finish(
                          t('issueMgmt.card.fixed'),
                          t('issueMgmt.card.fixedSaved', { title: issue.title }),
                          result.operationId,
                        );
                      },
                      onError: toast.notifyError,
                    },
                  );
                }}
              >
                {t('issueMgmt.card.fixedAction')}
              </Button>
              <Button
                size="lg"
                variant="secondary"
                loading={markNotFixed.isPending}
                disabled={busy}
                onClick={() => {
                  markNotFixed.mutate(
                    { issueId: issue.id },
                    {
                      onSuccess: (result) => {
                        finish(
                          t('issueMgmt.card.notFixed'),
                          t('issueMgmt.card.notFixedSaved', { title: issue.title }),
                          result.operationId,
                          issue.devRequired === true
                            ? t('issueMgmt.card.notFixedDetail', {
                                name: issue.assignee?.displayName ?? t('issueMgmt.common.dash'),
                              })
                            : undefined,
                        );
                      },
                      onError: toast.notifyError,
                    },
                  );
                }}
              >
                {t('issueMgmt.card.notFixedAction')}
              </Button>
            </div>
          ),
        };
      case 'reply': {
        const text = draft ?? replyDraft(issue, t);
        return {
          content: (
            <>
              <CardSection
                label={t('issueMgmt.card.replyTargets', { n: String(replyTargets.length) })}
              >
                <ul>
                  {replyTargets.map((voice) => (
                    <VoiceLine key={voice.pointId} voice={voice} now={now} />
                  ))}
                </ul>
              </CardSection>
              <CardSection label={t('issueMgmt.card.replyDraft')}>
                <Textarea
                  aria-label={t('issueMgmt.card.replyDraft')}
                  rows={4}
                  value={text}
                  onChange={(e) => {
                    setDraft(e.target.value);
                  }}
                />
              </CardSection>
            </>
          ),
          actions: (
            <div className="flex flex-wrap gap-2">
              <Button
                size="lg"
                loading={markReplied.isPending}
                disabled={busy || replyTargets.length === 0}
                onClick={() => {
                  markReplied.mutate(
                    { issueId: issue.id, pointIds: replyTargets.map((voice) => voice.pointId) },
                    {
                      onSuccess: (result) => {
                        finish(
                          t('issueMgmt.card.replied'),
                          t('issueMgmt.card.repliedSaved', { title: issue.title }),
                          result.operationId,
                        );
                      },
                      onError: toast.notifyError,
                    },
                  );
                }}
              >
                {t('issueMgmt.next.buttonReplied')}
              </Button>
              <Button
                size="lg"
                variant="secondary"
                onClick={() => {
                  copy(
                    text,
                    t('issueMgmt.card.replyCopied'),
                    t('issueMgmt.card.replyCopiedDetail'),
                  );
                }}
              >
                {t('issueMgmt.card.copy')}
              </Button>
            </div>
          ),
        };
      }
      case 'start_check':
        return {
          content: (
            <>
              <WhatSection issue={issue} />
              <StartNote issue={issue} now={now} />
            </>
          ),
          actions: (
            <>
              <Button
                size="lg"
                fullWidth
                onClick={() => {
                  setDialog({ kind: 'done' });
                }}
              >
                {t('issueMgmt.next.markDone')}
              </Button>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  loading={markInProgress.isPending}
                  disabled={busy}
                  onClick={() => {
                    markInProgress.mutate(
                      { issueId: issue.id },
                      {
                        onSuccess: (result) => {
                          finish(
                            t('issueMgmt.card.inProgress'),
                            t('issueMgmt.card.inProgressSaved', { title: issue.title }),
                            result.operationId,
                          );
                        },
                        onError: toast.notifyError,
                      },
                    );
                  }}
                >
                  {t('issueMgmt.card.inProgressAction')}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setDialog({ kind: 'decide', decision: 'accepted' });
                  }}
                >
                  {t('issueMgmt.card.reviewDecision')}
                </Button>
              </div>
            </>
          ),
        };
      default: {
        const unreachable: never = next;
        throw new Error(`unknown next action: ${JSON.stringify(unreachable)}`);
      }
    }
  })();

  return (
    <>
      <div className="px-5 pt-4">
        <h2 className="text-lg font-bold text-[var(--color-text-primary)]">
          {cardQuestion(card, t, issue.devRequired)}
        </h2>
        <p className="text-xs text-[var(--color-text-muted)]">{issue.id}</p>
      </div>
      {body.content}
      {!done && <Actions>{body.actions}</Actions>}
      {dialog?.kind === 'decide' && (
        <DecideDialog
          issue={issue}
          initialDecision={dialog.decision}
          now={now}
          onClose={() => {
            setDialog(null);
          }}
          onDecided={(label, operationId) => {
            outcome.onDone(label, operationId);
          }}
        />
      )}
      {dialog?.kind === 'link' && (
        <LinkCaseDialog
          issue={issue}
          now={now}
          onClose={() => {
            setDialog(null);
          }}
          onLinked={(caseKey, operationId) => {
            outcome.onDone(t('issueMgmt.card.linked', { key: caseKey }), operationId);
          }}
        />
      )}
      {dialog?.kind === 'done' && (
        <DoneDialog
          issue={issue}
          onClose={() => {
            setDialog(null);
          }}
          onDone={(label, operationId) => {
            outcome.onDone(label, operationId);
          }}
        />
      )}
    </>
  );
}
